import React, { useEffect, useRef, useState } from "react";
import "./styles.css";
import { db } from "./firebase";
import {
  doc,
  getDoc,
  onSnapshot,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import {
  buildRankedTagOptionsForBucket,
  DEFAULT_TEAM_ROUND_REVEAL_PAUSE_SEC,
  getTeamRoundChoiceSettingsFromLocalStorage,
  parseStartRoundTeamChoice,
  libraryIdsUsedInGame,
  placeholderNeedsForRound,
  placeholderScopeForRound,
  scheduleTeamChoiceOnStartRoundLanding,
} from "./roundTagChoice";

export const ROUND_TAG_SESSION_DOC = "session";

export default function TeamRoundChoiceGate({
  gameCode,
  current,
  questions,
  advance,
  venue,
}) {
  const [session, setSession] = useState(null);
  const [tick, setTick] = useState(0);
  const bootKeyRef = useRef(null);
  const revealHandledKeyRef = useRef(null);

  useEffect(() => {
    if (!gameCode || !Array.isArray(questions)) return undefined;
    const opts = getTeamRoundChoiceSettingsFromLocalStorage();
    if (!opts.enabled) return undefined;
    const tc = parseStartRoundTeamChoice(questions[current]);
    if (!tc.isTeamChoice) return undefined;
    (async () => {
      try {
        await scheduleTeamChoiceOnStartRoundLanding({
          gameCode,
          questions,
          currentIdx: current,
          settings: opts,
        });
      } catch (e) {
        console.warn("scheduleTeamChoiceOnStartRoundLanding", e);
      }
    })();
  }, [gameCode, questions, current]);

  useEffect(() => {
    if (!gameCode) return undefined;
    const ref = doc(db, "games", gameCode, "roundTagChoice", ROUND_TAG_SESSION_DOC);
    return onSnapshot(ref, (snap) => {
      const d = snap.exists() ? snap.data() : null;
      setSession(d);
      if (!d || d.phase === "idle") {
        bootKeyRef.current = null;
        revealHandledKeyRef.current = null;
      }
    });
  }, [gameCode]);

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 200);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!gameCode || !session || !Array.isArray(questions)) return;
    if (session.phase !== "scheduled") return;
    if (current !== session.startRoundIdx) return;
    const key = `boot-${session.startRoundIdx}-${session.endRoundIdx}`;
    if (bootKeyRef.current === key) return;

    let cancelled = false;
    (async () => {
      const opts = getTeamRoundChoiceSettingsFromLocalStorage();
      const isTestingVenue = venue === "Test Venue";
      const consumed =
        (session.consumedTagsByBucket &&
          session.consumedTagsByBucket[session.bucketKey]) ||
        [];
      try {
        // Library questions already used in this game can't fill the round, so they don't count.
        let excludeIds = new Set();
        try {
          const g = await getDoc(doc(db, "games", gameCode));
          excludeIds = libraryIdsUsedInGame(g.data());
        } catch {}
        const { options } = await buildRankedTagOptionsForBucket({
          genreBucketKey: session.bucketKey || "global",
          venue: venue || "Unknown Venue",
          minQuestionsPerTag: session.minQuestionsPerTag || opts.minQuestionsPerTag,
          maxOptions: 6,
          consumedTags: consumed,
          isTestingVenue,
          needByType: placeholderNeedsForRound(questions, session.startRoundIdx),
          scope: placeholderScopeForRound(questions, session.startRoundIdx),
          excludeIds,
        });
        if (cancelled) return;
        const tags = (options || []).map((o) => o.tag);
        // With no real team to choose (only simulated test teams), the topic is picked at random
        // after a short pause instead of waiting out the clock.
        const timeoutSec = session.eligibleTeam ? (session.timeoutSec || opts.timeoutSec) : 3;
        const deadlineAtMs = Date.now() + timeoutSec * 1000;
        bootKeyRef.current = key;
        await setDoc(
          doc(db, "games", gameCode, "roundTagChoice", ROUND_TAG_SESSION_DOC),
          {
            phase: "awaitingPick",
            tagOptions: tags,
            deadlineAtMs,
            chosenTag: null,
            pickSource: null,
            version: (session.version || 0) + 1,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
        try {
          window.__teamChoiceGate = true;
        } catch {}
        try {
          window.__overlayActive = true;
        } catch {}
      } catch (e) {
        console.warn("TeamRoundChoice bootstrap failed:", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [gameCode, session, current, questions, venue]);

  useEffect(() => {
    if (!session || session.phase !== "awaitingPick") return;
    if (session.chosenTag) return;
    if (!session.deadlineAtMs) return;
    if (Date.now() < session.deadlineAtMs) return;
    const tags = session.tagOptions;
    const pick = tags[Math.floor(Math.random() * tags.length)] || "";
    (async () => {
      const ref = doc(db, "games", gameCode, "roundTagChoice", ROUND_TAG_SESSION_DOC);
      const snap = await getDoc(ref);
      const d = snap.data() || {};
      if (d.phase !== "awaitingPick" || d.chosenTag) return;
      const startIdx = d.startRoundIdx;
      const map = { ...(d.chosenTagByStartRound || {}) };
      if (startIdx != null && pick) map[String(startIdx)] = pick;
      const opts = getTeamRoundChoiceSettingsFromLocalStorage();
      const revealMs = (opts.revealPauseSec || DEFAULT_TEAM_ROUND_REVEAL_PAUSE_SEC) * 1000;
      await setDoc(
        ref,
        {
          chosenTag: pick,
          pickSource: "timeoutRandom",
          phase: "revealPause",
          chosenTagByStartRound: map,
          revealUntilMs: Date.now() + revealMs,
          version: (d.version || 0) + 1,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    })();
  }, [session, tick, gameCode]);

  useEffect(() => {
    if (!session || session.phase !== "revealPause") return;
    const until = session.revealUntilMs || 0;
    if (Date.now() < until) return;
    const rk = `${session.version}-${until}`;
    if (revealHandledKeyRef.current === rk) return;
    revealHandledKeyRef.current = rk;
    (async () => {
      try {
        const ref = doc(db, "games", gameCode, "roundTagChoice", ROUND_TAG_SESSION_DOC);
        const live = await getDoc(ref);
        const d = live.data() || {};
        const b = d.bucketKey || "global";
        const tag = d.chosenTag || "";
        const prevConsumed = d.consumedTagsByBucket || {};
        const arr = Array.isArray(prevConsumed[b]) ? [...prevConsumed[b]] : [];
        if (tag) arr.push(tag);
        const nextConsumed = { ...prevConsumed, [b]: arr };
        await setDoc(
          ref,
          {
            phase: "idle",
            lastChooserTeam: d.eligibleTeam || null,
            consumedTagsByBucket: nextConsumed,
            version: (d.version || 0) + 1,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      } catch (e) {
        console.warn("TeamRoundChoice idle:", e);
      }
      try {
        window.__teamChoiceGate = false;
      } catch {}
      try {
        window.__overlayActive = false;
      } catch {}
      if (typeof advance === "function") {
        advance(1, false);
      }
    })();
  }, [session, tick, gameCode, advance]);

  const d = session;
  if (!d || (d.phase !== "awaitingPick" && d.phase !== "revealPause")) {
    return null;
  }

  const secsLeft =
    d.phase === "awaitingPick" && d.deadlineAtMs
      ? Math.max(0, Math.ceil((d.deadlineAtMs - Date.now()) / 1000))
      : d.phase === "revealPause" && d.revealUntilMs
        ? Math.max(0, Math.ceil((d.revealUntilMs - Date.now()) / 1000))
        : 0;

  const teamName = d.eligibleTeam || "";

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100000,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        padding:
          "max(6px, env(safe-area-inset-top)) 16px max(16px, env(safe-area-inset-bottom)) 16px",
        boxSizing: "border-box",
        background:
          "linear-gradient(180deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.75) 45%, rgba(0,0,0,0.92) 100%)",
        color: "#fff",
        pointerEvents: "auto",
        fontFamily: "system-ui, sans-serif",
        overflow: "hidden",
      }}
    >
      <div style={{ textAlign: "center", maxWidth: 1100, padding: "0 8px", marginBottom: 6 }}>
        <div
          style={{
            fontSize: "clamp(1rem, 2.8vw, 1.35rem)",
            fontWeight: 700,
            marginBottom: 4,
            lineHeight: 1.2,
          }}
        >
          {teamName ? "Please select the next round:" : "Picking the next round at random…"}
        </div>
        {teamName ? (
          <div
            style={{
              fontSize: "clamp(1.65rem, 5.5vw, 2.85rem)",
              fontWeight: 900,
              lineHeight: 1.15,
              textShadow: "0 2px 16px rgba(0,0,0,0.9)",
              wordBreak: "break-word",
            }}
          >
            {teamName}
          </div>
        ) : null}
      </div>
      {d.phase === "revealPause" && d.chosenTag ? (
        <div
          style={{
            marginBottom: 8,
            fontSize: "clamp(1rem, 2.8vw, 1.3rem)",
            fontWeight: 800,
            textAlign: "center",
          }}
        >
          Selected for this round:{" "}
          <strong style={{ color: "#86efac" }}>#{d.chosenTag}</strong>
        </div>
      ) : null}
      <div
        style={{
          fontSize: "min(10vw, 4rem)",
          fontWeight: 900,
          marginBottom: 8,
          textShadow: "0 2px 14px rgba(0,0,0,0.85)",
          lineHeight: 1,
        }}
      >
        {secsLeft}
      </div>
      <ol
        type="A"
        className="player-answer-list answers-stagger-in"
        style={{
          flex: "1 1 0",
          minHeight: 0,
          width: "100%",
          maxWidth: "min(1100px, 94vw)",
          overflowY: "auto",
          padding: 0,
          margin: 0,
          listStyle: "none",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: "2vh",
        }}
      >
        {(d.tagOptions || []).map((tg, displayIdx) => {
          const letter = String.fromCharCode(97 + (displayIdx % 6));
          const isReveal = d.phase === "revealPause";
          const isChosen = isReveal && d.chosenTag === tg;
          const isDimmed = isReveal && !isChosen;
          return (
            <li
              key={`${tg}-${displayIdx}`}
              style={{
                display: "flex",
                justifyContent: "center",
                width: "100%",
                flex: "1 1 0",
                minHeight: 0,
                margin: 0,
                padding: 0,
                listStyle: "none",
              }}
            >
              <div
                className={
                  "player-answer-option player-option-" +
                  letter +
                  (isChosen ? " correct" : "") +
                  (isDimmed ? " ghosted" : "")
                }
                style={{
                  width: "min(900px, 90vw)",
                  margin: "6px auto",
                  cursor: "default",
                  pointerEvents: "none",
                }}
              >
                #{tg}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
